import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Calendar, Users, MapPin, FolderKanban, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import FileGalleryFullScreen from '@/components/FileGalleryFullScreenNew';
import { formatErrorMessage } from '@/lib/errorHandler';
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AIFormFill from '@/components/AIFormFill';

const ELEGANT_GOLD = '#C9A961';

// Sortable Project Card Component
const SortableProjectCard = ({ project, getClientName, getUserName, getStatusBadge, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Set default values for backward compatibility
  const locationType = project.location_type || 'field';

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className="bg-dark-card border-0 shadow-card hover:shadow-gold hover:-translate-y-1 hover:bg-gradient-to-br hover:from-[#C9A961]/10 hover:to-transparent transition-all duration-200 cursor-pointer group mb-3"
    >
      <CardHeader className="p-4 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {locationType === 'field' ? (
              <MapPin className="h-4 w-4 text-[#C9A961]" />
            ) : (
              <Building2 className="h-4 w-4 text-[#C9A961]" />
            )}
            <Badge variant="outline" className="text-xs bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30">
              {locationType === 'field' ? '🏗️ Field' : '🏢 Office'}
            </Badge>
          </div>
          {getStatusBadge(project.status)}
        </div>
        <CardTitle className="text-base text-white group-hover:text-[#C9A961] transition-colors mt-2">
          {project.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-2">
        {project.description && (
          <p className="text-neutral-400 text-xs line-clamp-2">{project.description}</p>
        )}
        
        <div className="space-y-1.5 text-xs">
          {project.client_id && (
            <div className="flex items-center gap-2 text-neutral-400">
              <Users className="h-3 w-3" />
              <span>Client: {getClientName(project.client_id)}</span>
            </div>
          )}
          
          {project.address && (
            <div className="flex items-center gap-2 text-neutral-400">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{project.address}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-neutral-400">
            <Users className="h-3 w-3" />
            <span className="truncate">{project.assigned_to?.length || 0} team members</span>
          </div>
          
          {project.deadline && (
            <div className="flex items-center gap-2 text-neutral-400">
              <Calendar className="h-3 w-3" />
              <span>{new Date(project.deadline).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Kanban Column Component
const KanbanColumn = ({ status, projects, title, count, getClientName, getUserName, getStatusBadge, onProjectClick }) => {
  const { setNodeRef } = useSortable({ id: status });

  return (
    <div ref={setNodeRef} className="flex flex-col h-full min-w-[320px]">
      <div className="flex items-center justify-between mb-4 px-2">
        <h3 className="text-lg font-bold text-[#C9A961] flex items-center gap-2">
          {title}
          <Badge className="bg-[#C9A961]/20 text-[#C9A961] border-[#C9A961]/50">
            {count}
          </Badge>
        </h3>
      </div>
      <div className="flex-1 overflow-y-auto pr-2">
        <SortableContext items={projects.map(p => p.id)} strategy={verticalListSortingStrategy}>
          {projects.map((project) => (
            <SortableProjectCard
              key={project.id}
              project={project}
              getClientName={getClientName}
              getUserName={getUserName}
              getStatusBadge={getStatusBadge}
              onClick={(e) => {
                e.stopPropagation();
                onProjectClick(project);
              }}
            />
          ))}
        </SortableContext>
        {projects.length === 0 && (
          <div className="text-center py-8 text-neutral-500 text-sm">
            No projects
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectsPageKanban = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'field', 'office', 'all'
  const [activeId, setActiveId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location_type: 'field',
    address: '',
    client_id: '',
    deadline: '',
    status: 'not_started',
    assigned_to: [],
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isEmployee = currentUser.role === 'employee';
  const canFullEdit = !isEmployee;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes, usersRes] = await Promise.all([
        api.get('/projects'),
        api.get('/clients'),
        api.get('/users'),
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeProject = projects.find(p => p.id === active.id);
    const overStatus = over.id;

    // Check if we're dropping on a column or another project
    const isColumn = ['not_started', 'in_progress', 'completed', 'on_hold'].includes(overStatus);
    const newStatus = isColumn ? overStatus : projects.find(p => p.id === overStatus)?.status;

    if (activeProject && newStatus && activeProject.status !== newStatus) {
      try {
        await api.put(`/projects/${activeProject.id}`, { status: newStatus });
        toast.success('Project status updated');
        fetchData();
      } catch (error) {
        toast.error('Failed to update project status');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.location_type === 'field' && !formData.address) {
      toast.error('Address is required for field projects');
      return;
    }

    try {
      const data = { ...formData };
      if (data.deadline) {
        data.deadline = new Date(data.deadline).toISOString();
      }

      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, data);
        toast.success('Project updated successfully');
      } else {
        await api.post('/projects', data);
        toast.success('Project created successfully');
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Project deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      location_type: project.location_type || 'field',
      address: project.address || '',
      client_id: project.client_id || '',
      deadline: project.deadline ? new Date(project.deadline).toISOString().split('T')[0] : '',
      status: project.status || 'not_started',
      assigned_to: project.assigned_to || [],
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProject(null);
    setFormData({
      name: '',
      description: '',
      location_type: 'field',
      address: '',
      client_id: '',
      deadline: '',
      status: 'not_started',
      assigned_to: [],
    });
  };

  const handleAIFill = (filledData) => {
    setFormData({ ...formData, ...filledData });
  };

  const getStatusBadge = (status) => {
    const variants = {
      not_started: { label: 'Not Started', className: 'bg-neutral-500/20 text-neutral-300 border border-neutral-500/50' },
      in_progress: { label: 'Active', className: 'bg-[#C9A961]/20 text-[#C9A961] border border-[#C9A961]/50' },
      completed: { label: 'Completed', className: 'bg-[#C9A961]/30 text-[#C9A961] border border-[#C9A961]/50' },
      on_hold: { label: 'Inactive', className: 'bg-red-500/20 text-red-300 border border-red-500/50' },
    };
    const variant = variants[status] || variants.not_started;
    return <Badge className={`${variant.className} text-xs`}>{variant.label}</Badge>;
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? client.name : 'N/A';
  };

  const getUserName = (userIds) => {
    if (!Array.isArray(users)) return 'Unassigned';
    if (!userIds || userIds.length === 0) return 'Unassigned';
    
    const userIdArray = Array.isArray(userIds) ? userIds : [userIds];
    const userNames = userIdArray.map(userId => {
      const user = users.find((u) => u.id === userId);
      if (!user) return 'Unknown';
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`;
      }
      if (user.email && user.email.includes('@')) {
        const emailPart = user.email.split('@')[0];
        if (emailPart.includes('.')) {
          return emailPart.split('.').map(part => 
            part.charAt(0).toUpperCase() + part.slice(1)
          ).join(' ');
        }
      }
      return user.username.charAt(0).toUpperCase() + user.username.slice(1);
    });
    
    return userNames.join(', ');
  };

  // Filter projects based on active tab
  const filteredProjects = projects.filter(project => {
    const projectLocationType = project.location_type || 'field';
    
    // Tab filter
    if (activeTab === 'field' && projectLocationType !== 'field') return false;
    if (activeTab === 'office' && projectLocationType !== 'office') return false;
    
    return true;
  });

  const notStartedProjects = filteredProjects.filter(p => p.status === 'not_started');
  const activeProjects = filteredProjects.filter(p => p.status === 'in_progress');
  const completedProjects = filteredProjects.filter(p => p.status === 'completed');
  const inactiveProjects = filteredProjects.filter(p => p.status === 'on_hold');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div style={{ color: ELEGANT_GOLD }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 h-screen flex flex-col" data-testid="projects-page">
      {/* Header Section with Company Logo */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <img src="/williams-logo-transparent.png" alt="Williams Diversified" className="h-16" />
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3 font-serif">
              <FolderKanban className="h-10 w-10 text-[#C9A961]" />
              <span className="text-white">Project</span>{' '}
              <span className="text-[#C9A961] italic">Management</span>
            </h1>
            <p className="text-neutral-400 text-lg">
              Manage all your construction projects {isEmployee ? '(View Only)' : ''}
            </p>
          </div>
        </div>
        {canFullEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => setEditingProject(null)} 
                className="bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all shadow-lg"
              >
                <Plus className="mr-2 h-5 w-5" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-dark-card border-[#C9A961]/30 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl text-[#C9A961] flex items-center gap-2">
                  <FolderKanban className="h-6 w-6" />
                  {editingProject ? 'Edit Project' : 'Create New Project'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="flex justify-end mb-4">
                  <AIFormFill
                    formData={formData}
                    onFillComplete={handleAIFill}
                    context="project creation form"
                  />
                </div>

                {/* Project Location Type */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Project Location Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className={`h-12 justify-start ${formData.location_type === 'field' ? 'bg-[#C9A961]/20 border-[#C9A961] text-[#C9A961]' : 'border-neutral-600 text-neutral-400'}`}
                      onClick={() => setFormData({ ...formData, location_type: 'field' })}
                    >
                      <MapPin className="mr-2 h-4 w-4" />
                      🏗️ Field Project
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className={`h-12 justify-start ${formData.location_type === 'office' ? 'bg-[#C9A961]/20 border-[#C9A961] text-[#C9A961]' : 'border-neutral-600 text-neutral-400'}`}
                      onClick={() => setFormData({ ...formData, location_type: 'office' })}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      🏢 Office Project
                    </Button>
                  </div>
                </div>

                {/* Project Name */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Project Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]"
                    placeholder="Enter project name"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]"
                    placeholder="Project description"
                    rows={4}
                  />
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required={formData.location_type === 'field'}
                    className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]"
                    placeholder={formData.location_type === 'field' ? 'Project location (required for field projects)' : 'Project location (optional for office projects)'}
                  />
                </div>

                {/* Assign Team Members */}
                <div className="space-y-2">
                  <Label className="text-[#C9A961]">Assign Team Members</Label>
                  <Select 
                    value={formData.assigned_to.length > 0 ? formData.assigned_to[formData.assigned_to.length - 1] : ''} 
                    onValueChange={(value) => {
                      if (value && !formData.assigned_to.includes(value)) {
                        setFormData({ ...formData, assigned_to: [...formData.assigned_to, value] });
                      }
                    }}
                  >
                    <SelectTrigger className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]">
                      <SelectValue placeholder="Select team members to assign" />
                    </SelectTrigger>
                    <SelectContent className="bg-dark-card border-[#C9A961]/30">
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id} className="text-white hover:bg-[#C9A961]/20">
                          {user.first_name} {user.last_name} ({user.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Selected Users */}
                  {formData.assigned_to.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.assigned_to.map((userId) => {
                        const user = users.find(u => u.id === userId);
                        return user ? (
                          <Badge 
                            key={userId} 
                            className="bg-[#C9A961]/20 text-[#C9A961] border border-[#C9A961]/50 px-3 py-1 flex items-center gap-2"
                          >
                            {user.first_name} {user.last_name}
                            <button
                              type="button"
                              onClick={() => setFormData({ 
                                ...formData, 
                                assigned_to: formData.assigned_to.filter(id => id !== userId) 
                              })}
                              className="hover:text-red-400 transition-colors"
                            >
                              ×
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  <p className="text-xs text-neutral-500 mt-2">
                    📧 Selected users will receive email notifications automatically
                  </p>
                </div>

                {/* Client and Deadline */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#C9A961]">Client</Label>
                    <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })}>
                      <SelectTrigger className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent className="bg-dark-card border-[#C9A961]/30">
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id} className="text-white hover:bg-[#C9A961]/20">
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#C9A961]">Deadline</Label>
                    <Input
                      type="date"
                      value={formData.deadline}
                      onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                      className="bg-black/50 border-0 text-white focus:ring-2 focus:ring-[#C9A961]"
                    />
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all">
                    {editingProject ? 'Update Project' : 'Create Project'}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-[#C9A961]/20 pb-2">
        <Button
          variant="ghost"
          className={`${activeTab === 'field' ? 'text-[#C9A961] border-b-2 border-[#C9A961]' : 'text-neutral-400'} rounded-none`}
          onClick={() => setActiveTab('field')}
        >
          🏗️ Field Projects ({projects.filter(p => (p.location_type || 'field') === 'field').length})
        </Button>
        <Button
          variant="ghost"
          className={`${activeTab === 'office' ? 'text-[#C9A961] border-b-2 border-[#C9A961]' : 'text-neutral-400'} rounded-none`}
          onClick={() => setActiveTab('office')}
        >
          🏢 Office Projects ({projects.filter(p => (p.location_type || 'field') === 'office').length})
        </Button>
        <Button
          variant="ghost"
          className={`${activeTab === 'all' ? 'text-[#C9A961] border-b-2 border-[#C9A961]' : 'text-neutral-400'} rounded-none`}
          onClick={() => setActiveTab('all')}
        >
          All Projects ({projects.length})
        </Button>
      </div>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-hidden">
          <div className="flex gap-6 h-full overflow-x-auto pb-4">
            <SortableContext items={['not_started', 'in_progress', 'completed', 'on_hold']} strategy={verticalListSortingStrategy}>
              <KanbanColumn
                status="not_started"
                title="Not Started"
                count={notStartedProjects.length}
                projects={notStartedProjects}
                getClientName={getClientName}
                getUserName={getUserName}
                getStatusBadge={getStatusBadge}
                onProjectClick={(project) => {
                  setSelectedProject(project);
                  setGalleryOpen(true);
                }}
              />
              <KanbanColumn
                status="in_progress"
                title="Active"
                count={activeProjects.length}
                projects={activeProjects}
                getClientName={getClientName}
                getUserName={getUserName}
                getStatusBadge={getStatusBadge}
                onProjectClick={(project) => {
                  setSelectedProject(project);
                  setGalleryOpen(true);
                }}
              />
              <KanbanColumn
                status="completed"
                title="Completed"
                count={completedProjects.length}
                projects={completedProjects}
                getClientName={getClientName}
                getUserName={getUserName}
                getStatusBadge={getStatusBadge}
                onProjectClick={(project) => {
                  setSelectedProject(project);
                  setGalleryOpen(true);
                }}
              />
              <KanbanColumn
                status="on_hold"
                title="Inactive"
                count={inactiveProjects.length}
                projects={inactiveProjects}
                getClientName={getClientName}
                getUserName={getUserName}
                getStatusBadge={getStatusBadge}
                onProjectClick={(project) => {
                  setSelectedProject(project);
                  setGalleryOpen(true);
                }}
              />
            </SortableContext>
          </div>
        </div>

        <DragOverlay>
          {activeId ? (
            <Card className="bg-dark-card border-0 shadow-gold w-[320px] opacity-90">
              <CardContent className="p-4">
                <p className="text-white font-semibold">
                  {projects.find(p => p.id === activeId)?.name}
                </p>
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Full Screen Project View */}
      {selectedProject && (
        <FileGalleryFullScreen
          isOpen={galleryOpen}
          onClose={() => {
            setGalleryOpen(false);
            setSelectedProject(null);
          }}
          record={selectedProject}
          recordType="project"
          files={selectedProject.files || []}
          onDelete={handleDelete}
          canDelete={canFullEdit}
          onUpdate={fetchData}
          users={users}
        />
      )}
    </div>
  );
};

export default ProjectsPageKanban;
